import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useMotionValue, useSpring, AnimatePresence } from 'framer-motion';
import '../styles/CustomCursor.scss';

const TRAIL_LIMIT = 8;
let trailId = 0;

function useIsTouch() {
  const [isTouch, setIsTouch] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  });

  useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    const handler = (e) => setIsTouch(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isTouch;
}

const CustomCursor = () => {
  const isTouch = useIsTouch();
  const [trail, setTrail] = useState([]);
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const lastPos = useRef({ x: -200, y: -200 });
  const frameRef = useRef(null);
  const pendingTrail = useRef([]);

  const mouseX = useMotionValue(-200);
  const mouseY = useMotionValue(-200);
  const ringX = useSpring(mouseX, { damping: 28, stiffness: 180, mass: 0.8 });
  const ringY = useSpring(mouseY, { damping: 28, stiffness: 180, mass: 0.8 });

  const addTrailPoint = useCallback((x, y) => {
    const dx = x - lastPos.current.x;
    const dy = y - lastPos.current.y;
    if (Math.sqrt(dx * dx + dy * dy) < 6) return;
    lastPos.current = { x, y };
    pendingTrail.current.push({
      id: trailId++,
      x: x + (Math.random() - 0.5) * 6,
      y: y + (Math.random() - 0.5) * 6,
      size: Math.random() * 3 + 3,
    });
  }, []);

  useEffect(() => {
    if (isTouch) return undefined;

    const flush = () => {
      if (pendingTrail.current.length > 0) {
        const incoming = pendingTrail.current.splice(0);
        setTrail((prev) => [...prev, ...incoming].slice(-TRAIL_LIMIT));
      }
      frameRef.current = requestAnimationFrame(flush);
    };
    frameRef.current = requestAnimationFrame(flush);
    return () => cancelAnimationFrame(frameRef.current);
  }, [isTouch]);

  useEffect(() => {
    if (isTouch) return undefined;

    const selectors = "a, button, [role='button'], input, textarea, select, label, .btn";
    const onMove = (e) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      addTrailPoint(e.clientX, e.clientY);
      setIsVisible(true);
    };
    const onLeave = () => setIsVisible(false);
    const onEnter = () => setIsVisible(true);
    const onOver = (e) => {
      if (e.target.closest(selectors)) setIsHovering(true);
    };
    const onOut = (e) => {
      if (e.target.closest(selectors)) setIsHovering(false);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseleave', onLeave);
    window.addEventListener('mouseenter', onEnter);
    window.addEventListener('mouseover', onOver);
    window.addEventListener('mouseout', onOut);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('mouseenter', onEnter);
      window.removeEventListener('mouseover', onOver);
      window.removeEventListener('mouseout', onOut);
    };
  }, [isTouch, mouseX, mouseY, addTrailPoint]);

  if (isTouch) return null;

  return (
    <div className="custom-cursor">
      <AnimatePresence>
        {trail.map((p) => (
          <motion.div
            key={p.id}
            className="custom-cursor__trail"
            initial={{ opacity: 0.55, scale: 1 }}
            animate={{ opacity: 0, scale: 0.25 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.42, ease: 'easeOut' }}
            onAnimationComplete={() =>
              setTrail((prev) => prev.filter((t) => t.id !== p.id))
            }
            style={{
              left: p.x,
              top: p.y,
              width: p.size,
              height: p.size,
            }}
          />
        ))}
      </AnimatePresence>

      <motion.div
        className={`custom-cursor__ring ${isHovering ? 'custom-cursor__ring--hover' : ''}`}
        style={{
          left: ringX,
          top: ringY,
          opacity: isVisible ? 1 : 0,
        }}
      />

      <motion.div
        className={`custom-cursor__dot ${isHovering ? 'custom-cursor__dot--hover' : ''}`}
        style={{
          left: mouseX,
          top: mouseY,
          opacity: isVisible ? 1 : 0,
        }}
      />
    </div>
  );
};

export default CustomCursor;
